import styles from "../../../../styles/races/national/MapWidget.module.scss";
import { useState, useEffect, useCallback } from "react";
import phMapDataGeo from "./phlmap";
import {
  ZoomableGroup,
  ComposableMap,
  Geographies,
  Geography
} from "react-simple-maps";

const MapPhilippines = ({ 
  region,
  province,
  onProvinceClick,
  onProvinceHover,
  disabled,
  locationData, 
  colorFilter,
  mapType 
}) => {
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 0]);
  const [isMounted, setIsMounted] = useState(false);
  
  const projectionConfig = {
    rotate: [-7.25, 23, 10],
    scale: 2000,
    center: [120, 10.6],
    parallels: [10, 15]
  }

  const getLocationColor = useCallback((locationSlug) => {
    const locationDataPoint = locationData.find((locationDataPoint) => locationDataPoint.locationSlug === locationSlug);
    if(mapType === "votes" && locationDataPoint?.leadingCandidateColor) {
      return locationDataPoint.leadingCandidateColor;
    } else if(mapType === "transmission-status" && locationDataPoint?.transmissionStatus?.color) {
      return locationDataPoint.transmissionStatus.color;
    }
  }, [locationData, mapType]);
  
  const handleProvinceClick = ({ geo, projection, path }) => event => {
    event.stopPropagation();
    if(disabled) {
      return;
    }

    let regionSlug = geo.properties.ADM1_SLUG;
    let provinceSlug = geo.properties.ADM2_SLUG;
    let citySlug = geo.properties.ADM3_SLUG;

    if(!regionSlug && geo.properties.ADM2_EN.includes("NCR")) {
      regionSlug = "ncr";
    }

    onProvinceClick({ 
      regionSlug, 
      provinceSlug,
      citySlug 
    });
    centerOnLocation({ geo, projection, path });
  };

  const centerOnLocation = ({ geo, projection, path }) => {
    const [x, y] = path.centroid(geo);
    const centroid = projection.invert([x + 30, y]);
    const _zoom = 3;
    const locationSlug = geo.properties.ADM3_SLUG || geo.properties.ADM2_SLUG || geo.properties.ADM1_SLUG;
    const regionSlug = geo.properties.ADM1_SLUG;

    zoomTo(centroid, _zoom, locationSlug, regionSlug);
  };

  const zoomTo = (centroid, zoom, locationSlug, regionSlug) => {
    let _zoom = zoom;

    if(regionSlug === "mimaropa") {
      _zoom = 1.8;
      if(locationSlug === "mimaropa") {
        centroid = [121.3584, 12.2638];
      }
    }

    if(regionSlug === "barmm") {
      _zoom = 1.7;
      if(locationSlug === "barmm") {
        centroid = [123.4437, 6.6054];
      }
    }

    if(regionSlug === "cagayan-valley") {
      _zoom = 2.4;
      if(locationSlug === "cagayan-valley") {
        centroid = [122.8670, 18.5583];
      }
    }

    setCenter(centroid);
    setZoom(_zoom);
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if(disabled) {
      return;
    }
    
    if(!province || !province.slug) {
      return;
    }

    const geography = document.querySelector(`[data-id='${province.slug}'`);
    const click = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: false
    });

    if(geography) {
      geography.dispatchEvent(click);
    }

  }, [disabled, province]);

  useEffect(() => {
    resetZoom();
  }, [disabled]);

  useEffect(() => {
    if(disabled) {
      return;
    }
    
    if(!region) {
      resetZoom();
    } else {
      if(!isMounted) {
        return;
      }
      const geo = document.querySelector(`[data-region='${region.slug}'`);
      const centroid = geo.getAttribute("data-centroid").split(",").map((coord) => parseFloat(coord));
      zoomTo(centroid, 3, region.slug, region.slug);
    }
  }, [disabled, region, isMounted]);

  const resetZoom = () => {
    setZoom(1);
    setCenter([124.03503, 12.65945]);
  }

  const isSelected = useCallback((geo) => {
    const isSelectedRegion = false;
    const isSelectedProvince = false;
    const isSelectedCity = false;

    if(region && region.slug && region.slug === geo.properties.ADM1_SLUG) {
      isSelectedRegion = true;
    }

    if(province && province.slug && province.slug === geo.properties.ADM3_SLUG) {
      isSelectedCity = true;
    } else if(province && province.slug && province.slug === geo.properties.ADM2_SLUG) {
      isSelectedProvince = true;
    }

    return { isSelectedRegion, isSelectedProvince, isSelectedCity };
  }, [province, region]);

  const getStyle = useCallback((geo) => {
    const defaultColor = disabled ? "#C9D8FD" : "#C9D8FD";
    const defaultStroke = disabled ? "#FFFFFF" : "#FFFFFF";
    const style = {
      default: {
        fill: defaultColor,
        stroke: defaultStroke,
        strokeWidth: "0.25",
        outline: "none"
      },
      hover: {
        fill: "#DCDEF6",
        stroke: "transparent",
        strokeWidth: "0.25",
        outline: "none"
      },
      pressed: {
        fill: "#FFF",
        stroke: defaultColor,
        strokeWidth: "0.25",
        outline: "none"
      }
    };

    const { isSelectedRegion, isSelectedProvince, isSelectedCity } = isSelected(geo);
    const locationSlug = geo.properties.ADM2_EN.includes("NCR") 
      ? "ncr" 
      : (geo.properties.ADM3_SLUG || geo.properties.ADM2_SLUG);
    const locationColor = getLocationColor(locationSlug) || defaultColor;

    let locationColorFaded = "#DCDEF6";
    
    if(locationColor !== defaultColor) {  
      const opacityValue = mapType === "votes" ? 0.5 : 0.3;
      const rgb = locationColor.slice(locationColor.indexOf("(") + 1, locationColor.lastIndexOf(",")).split(", ");
      const fadedRgb = rgb.map((value) => {
        const newValue = value * opacityValue + (255 * (1 - opacityValue));
        return newValue > 255 ? 255 : Math.floor(newValue);
      });
      locationColorFaded = `rgba(${fadedRgb.join(", ")}, 1)`;
    }
    const inFilter = locationColor === defaultColor || !!colorFilter.find(color => color === locationColor) || mapType === "transmission-status";

    if((disabled || (locationSlug === "ncr" && isSelectedRegion)) && inFilter) {
      style.default.fill = locationColor;
      style.hover.fill = locationColor;
    }

    // Has selected region but not this region
    else if(region && !isSelectedRegion) {
      style.default.fill = defaultColor;
      style.hover.fill = locationColorFaded;
    }

    // Is Selected Province
    else if(!disabled && ((isSelectedProvince && !geo.properties.ADM3_SLUG) || isSelectedCity)) {
      if(inFilter) {
        style.default.fill = locationColor === defaultColor ? "#F5F6FF" : locationColorFaded;
        style.hover.fill = style.default.fill;
      }
      // style.hover.fill = locationColorFaded;
    }

    // Minimapped or Entire Philippines Selected OR Selection is in selected region
    else if((disabled || !region || isSelectedRegion)) {
      if(inFilter) {
        style.default.fill = locationColor;
      }
      if(!disabled) {
        style.hover.fill = locationColorFaded;
      }
    }
    
    else {
      style.default.fill = defaultColor;
    }

    if(
      (isSelectedProvince && !geo.properties.ADM3_SLUG) || isSelectedCity
    ) {
      // style.default.stroke = locationColor;
      style.default.stroke = "#172038";
      style.default.strokeWidth = "0.5";
      if(!disabled) {
        style.hover.stroke = "#172038";
        style.hover.strokeWidth = "0.5";
      }
    }

    if(locationColor !== defaultColor && !disabled) {
      // style.hover.stroke = locationColor;
    }

    return style;
  }, [colorFilter, disabled, getLocationColor, isSelected, mapType, region]);

  const handleFilter = ({ constructor: { name }}) => {
    return !disabled && (zoom > 1 || (name !== "MouseEvent" && name !== "TouchEvent"));
  };

  const handleMouseEnter = (geo) => event => {
    if(disabled) {
      return;
    }
    const locationSlug = geo.properties.ADM2_EN.includes("NCR") ? "ncr" : (geo.properties.ADM3_SLUG || geo.properties.ADM2_SLUG);
    const location = locationData.find(
      (locationDataPoint) => locationDataPoint.locationSlug === locationSlug
    );
    let transmissionPercentage = 0;

    if(location && location.transmissionStatus) {
      transmissionPercentage = location.transmissionStatus.percentage || 0;
    }

    onProvinceHover({ 
      name: `${geo.properties.ADM2_EN.includes("NCR") ? "NCR" : (geo.properties.ADM3_EN || geo.properties.ADM2_EN)}`,
      location: locationSlug,
      transmissionPercentage
    });
  }

  const handleMouseLeave = () => {
    if(disabled) {
      return;
    }
    onProvinceHover({});
  }

  return (
    <div className={styles.mapContainer}>
      <ComposableMap 
        data-tip="" 
        projection="geoAlbers" 
        width={600} 
        height={670} 
        style={{ height: "auto", width: "150%" }} 
        projectionConfig={projectionConfig}
        onClick={() => { onProvinceClick({}) }}
      >
        <ZoomableGroup
          center={center} 
          zoom={zoom} 
          minZoom={zoom} 
          maxZoom={zoom}
          filterZoomEvent={handleFilter}
        >
          <Geographies geography={phMapDataGeo}>
            {({ geographies, projection, path }) => (
              <>
                {geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    data-id={`${geo.properties.ADM3_SLUG || geo.properties.ADM2_SLUG}`}
                    data-region={`${geo.properties.ADM1_SLUG}`}
                    data-centroid={(() => {
                      const [x, y] = path.centroid(geo);
                      let centroid = projection.invert([x + 30, y]);
                      return centroid;
                    })()}
                    geography={geo}
                    onClickCapture={handleProvinceClick({ geo, projection, path })}
                    onMouseEnter={handleMouseEnter(geo)}
                    onMouseLeave={handleMouseLeave}
                    style={getStyle(geo)}
                  />
                ))}
                {
                  geographies.map((geo) => {
                    if(isSelected(geo).isSelectedProvince || isSelected(geo).isSelectedCity) {
                      return <Geography                       
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={handleProvinceClick({ geo, projection, path })}
                        onMouseEnter={handleMouseEnter(geo)}
                        onMouseLeave={handleMouseLeave}
                        style={getStyle(geo)}
                      />
                    }
                  })
                }
              </>
            )}
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  )
};

export default MapPhilippines;